import { useState } from 'react';
import { type TrendEvent } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { usePersistedTableState } from '../../hooks/usePersistedTableState';
import { EmptyState } from '../EmptyState';
import { SellerCell } from './SellerCell';
import { TrendEventModal } from './TrendEventModal';
import { TruncatedCell } from './TruncatedCell';

const rubFmt = new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
  maximumFractionDigits: 0,
});

function formatRub(v: number | null | undefined): string {
  if (v == null) return '—';
  return rubFmt.format(v);
}

function formatGrowth(v: number | null | undefined): string {
  if (v == null) return '—';
  return `${(v * 100).toFixed(0)}%`;
}

function formatNumber(v: number | null | undefined): string {
  if (v == null) return '—';
  return new Intl.NumberFormat('ru-RU').format(v);
}

function present(value: string | null): value is string {
  return Boolean(value);
}

type EventsSortBy =
  | 'sku'
  | 'title'
  | 'brand'
  | 'seller'
  | 'category'
  | 'revenue'
  | 'previousRevenue'
  | 'growth'
  | 'price'
  | 'comments';

type EventsTableState = {
  brandFilter: string;
  sellerFilter: string;
  categoryFilter: string;
  sortBy: EventsSortBy;
  sortDir: 'asc' | 'desc';
};

const EVENTS_TABLE_DEFAULTS: EventsTableState = {
  brandFilter: '',
  sellerFilter: '',
  categoryFilter: '',
  sortBy: 'revenue',
  sortDir: 'desc',
};
const EVENTS_TABLE_SORTING_KEYS: readonly (keyof EventsTableState)[] = ['sortBy', 'sortDir'];
const EVENTS_TABLE_FILTER_KEYS: readonly (keyof EventsTableState)[] = [
  'brandFilter',
  'sellerFilter',
  'categoryFilter',
];

function compareNullableNumber(
  a: number | null | undefined,
  b: number | null | undefined,
  direction: 'asc' | 'desc',
): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return direction === 'asc' ? a - b : b - a;
}

function compareText(
  a: string | null | undefined,
  b: string | null | undefined,
  direction: 'asc' | 'desc',
): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  const result = a.localeCompare(b, 'ru');
  return direction === 'asc' ? result : -result;
}

export function EventsTable({
  events,
  showGrowth,
  tableId,
}: {
  events: TrendEvent[];
  showGrowth: boolean;
  tableId: string;
}) {
  const { user } = useAuth();
  const [tableState, setTableState] = usePersistedTableState(
    user?.id,
    tableId,
    EVENTS_TABLE_DEFAULTS,
    {
      sortingKeys: EVENTS_TABLE_SORTING_KEYS,
      filterKeys: EVENTS_TABLE_FILTER_KEYS,
      persistSorting: Boolean(user?.persist_table_sorting),
      persistFilters: Boolean(user?.persist_table_filters),
    },
  );
  const [selected, setSelected] = useState<TrendEvent | null>(null);
  const { brandFilter, sellerFilter, categoryFilter, sortBy, sortDir } = tableState;

  function updateTableState(patch: Partial<EventsTableState>) {
    setTableState((prev) => ({ ...prev, ...patch }));
  }

  function updateSort(key: EventsSortBy) {
    if (sortBy === key) {
      updateTableState({ sortDir: sortDir === 'asc' ? 'desc' : 'asc' });
    } else {
      updateTableState({ sortBy: key, sortDir: 'asc' });
    }
  }

  function SortHeader({
    label,
    value,
    className = '',
  }: {
    label: string;
    value: EventsSortBy;
    className?: string;
  }) {
    const active = sortBy === value;
    return (
      <button
        type="button"
        className={`text-caption text-text-secondary hover:text-text-primary ${className}`}
        onClick={() => updateSort(value)}
      >
        {label}
        {active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
      </button>
    );
  }

  if (events.length === 0) {
    return (
      <EmptyState
        title="Карточек не найдено"
        description="Пороги или категории могли не дать событий этого типа."
      />
    );
  }

  const brands = Array.from(new Set(events.map((e) => e.brand).filter(present))).sort();
  const sellers = Array.from(new Set(events.map((e) => e.seller).filter(present))).sort();
  const categories = Array.from(new Set(events.map((e) => e.category_path).filter(present))).sort();
  const filtered = events
    .filter((e) => !brandFilter || e.brand === brandFilter)
    .filter((e) => !sellerFilter || e.seller === sellerFilter)
    .filter((e) => !categoryFilter || e.category_path === categoryFilter)
    .sort((a, b) => {
      if (sortBy === 'sku') return compareText(a.sku_external_id, b.sku_external_id, sortDir);
      if (sortBy === 'title') return compareText(a.sku_title, b.sku_title, sortDir);
      if (sortBy === 'brand') return compareText(a.brand, b.brand, sortDir);
      if (sortBy === 'seller') return compareText(a.seller, b.seller, sortDir);
      if (sortBy === 'category') return compareText(a.category_path, b.category_path, sortDir);
      if (sortBy === 'previousRevenue') {
        return compareNullableNumber(a.revenue_previous_rub, b.revenue_previous_rub, sortDir);
      }
      if (sortBy === 'growth') return compareNullableNumber(a.growth_rate, b.growth_rate, sortDir);
      if (sortBy === 'price') {
        return compareNullableNumber(a.final_price_current, b.final_price_current, sortDir);
      }
      if (sortBy === 'comments') {
        return compareNullableNumber(a.comments_current, b.comments_current, sortDir);
      }
      return compareNullableNumber(a.revenue_current_rub, b.revenue_current_rub, sortDir);
    });

  return (
    <div className="flex flex-col gap-3">
      <div className="card card-padding grid gap-3 md:grid-cols-4">
        <select
          className="input"
          value={brandFilter}
          onChange={(e) => updateTableState({ brandFilter: e.target.value })}
        >
          <option value="">Все бренды</option>
          {brands.map((brand) => (
            <option key={brand} value={brand}>
              {brand}
            </option>
          ))}
        </select>
        <select
          className="input"
          value={sellerFilter}
          onChange={(e) => updateTableState({ sellerFilter: e.target.value })}
        >
          <option value="">Все продавцы</option>
          {sellers.map((seller) => (
            <option key={seller} value={seller}>
              {seller}
            </option>
          ))}
        </select>
        <select
          className="input"
          value={categoryFilter}
          onChange={(e) => updateTableState({ categoryFilter: e.target.value })}
        >
          <option value="">Все категории</option>
          {categories.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>
        <div className="text-caption text-text-tertiary flex items-center">
          Показано {filtered.length} из {events.length}
        </div>
      </div>

      <div className="table-wrap">
        <table className="table table-fixed min-w-[1690px]">
          <colgroup>
            <col className="w-[108px]" />
            <col className="w-[360px]" />
            <col className="w-[180px]" />
            <col className="w-[250px]" />
            <col className="w-[300px]" />
            <col className="w-[140px]" />
            <col className="w-[140px]" />
            {showGrowth ? <col className="w-[96px]" /> : null}
            <col className="w-[120px]" />
            <col className="w-[92px]" />
          </colgroup>
          <thead>
            <tr>
              <th className="whitespace-nowrap"><SortHeader label="SKU" value="sku" /></th>
              <th><SortHeader label="Название" value="title" /></th>
              <th><SortHeader label="Бренд" value="brand" /></th>
              <th><SortHeader label="Продавец" value="seller" /></th>
              <th><SortHeader label="Категория" value="category" /></th>
              <th className="whitespace-nowrap text-right">
                <SortHeader label="Заказы, ₽" value="revenue" />
              </th>
              <th className="text-right">
                <SortHeader label="Заказы ранее, ₽" value="previousRevenue" />
              </th>
              {showGrowth ? (
                <th className="whitespace-nowrap text-right">
                  <SortHeader label="Рост" value="growth" />
                </th>
              ) : null}
              <th className="whitespace-nowrap text-right">
                <SortHeader label="Цена" value="price" />
              </th>
              <th className="whitespace-nowrap text-right">
                <SortHeader label="Отзывы" value="comments" />
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((event) => {
              const externalUrl = event.product_details?.external_url ?? event.external_url;
              return (
                <tr
                  key={event.id}
                  className="cursor-pointer hover:bg-surface-2"
                  onClick={() => setSelected(event)}
                >
                  <td className="mono whitespace-nowrap">
                    {externalUrl ? (
                      <a
                        className="block truncate text-accent hover:underline"
                        href={externalUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(clickEvent) => clickEvent.stopPropagation()}
                      >
                        {event.sku_external_id}
                      </a>
                    ) : (
                      event.sku_external_id
                    )}
                  </td>
                  <td className="overflow-hidden text-text-primary">
                    <TruncatedCell value={event.sku_title} className="max-w-full" />
                  </td>
                  <td className="overflow-hidden">
                    <TruncatedCell value={event.brand} className="max-w-full" />
                  </td>
                  <td className="overflow-hidden">
                    <SellerCell name={event.seller} externalId={event.seller_external_id} />
                  </td>
                  <td className="mono overflow-hidden text-caption">
                    <TruncatedCell value={event.category_path} className="max-w-full" />
                  </td>
                  <td className="mono whitespace-nowrap text-right text-text-primary">
                    {formatRub(event.revenue_current_rub)}
                  </td>
                  <td className="mono whitespace-nowrap text-right">{formatRub(event.revenue_previous_rub)}</td>
                  {showGrowth ? (
                    <td className="mono whitespace-nowrap text-right text-accent">
                      {formatGrowth(event.growth_rate)}
                    </td>
                  ) : null}
                  <td className="mono whitespace-nowrap text-right">{formatRub(event.final_price_current)}</td>
                  <td className="mono whitespace-nowrap text-right">{formatNumber(event.comments_current)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {selected ? <TrendEventModal event={selected} onClose={() => setSelected(null)} /> : null}
    </div>
  );
}
